import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../utils/api";
import api from "../../services/api";
import "../../styles/Auth.css";
import { clearMessage } from "./decorators";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useLanguage } from "../../utils/LanguageContext";

const REGIONS = [
  ["Qoraqalpogʻiston Respublikasi", "auth_region_qoraqalpaq"],
  ["Andijon viloyati", "auth_region_andijan"],
  ["Buxoro viloyati", "auth_region_bukhara"],
  ["Fargʻona viloyati", "auth_region_fergana"],
  ["Jizzax viloyati", "auth_region_jizzakh"],
  ["Xorazm viloyati", "auth_region_khorezm"],
  ["Namangan viloyati", "auth_region_namangan"],
  ["Navoiy viloyati", "auth_region_navoiy"],
  ["Qashqadaryo viloyati", "auth_region_kashkadarya"],
  ["Samarqand viloyati", "auth_region_samarkand"],
  ["Sirdaryo viloyati", "auth_region_sirdaryo"],
  ["Surxondaryo viloyati", "auth_region_surkhandarya"],
  ["Toshkent viloyati", "auth_region_tashkent_reg"],
  ["Toshkent shahri", "auth_region_tashkent_city"],
];

export default function Auth() {
  const [searchParams] = useSearchParams();
  // Check URL for ?mode=login or ?mode=register, default to register
  const initialMode = searchParams.get('mode') === 'login' ? 'login' : 'register';
  // Where to go after a successful sign-in (e.g. back to the shop)
  const nextPath = searchParams.get('next') || '/profile';
  const [action, setAction] = useState(initialMode);
  const [step, setStep] = useState(1); // registration is split into two short steps
  const [password, setPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [prefillUsername, setPrefillUsername] = useState("");
  const stepOneRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    setFormError("");
    setStep(1);
  }, [action]);

  const handlePhoneChange = (e) => {
    let val = e.target.value;
    let digits = val.replace(/\D/g, "");

    if (digits.length === 0) {
      setPhoneNumber("");
      return;
    }

    if (!digits.startsWith("998")) {
      digits = "998" + digits;
    }

    let formatted = "+998";
    if (digits.length > 3) {
      formatted += " (" + digits.substring(3, 5);
    }
    if (digits.length > 5) {
      formatted += ") " + digits.substring(5, 8);
    }
    if (digits.length > 8) {
      formatted += "-" + digits.substring(8, 10);
    }
    if (digits.length > 10) {
      formatted += "-" + digits.substring(10, 12);
    }

    setPhoneNumber(formatted);
  };

  // Validate the first step's fields before moving on.
  const goToStepTwo = () => {
    const fields = stepOneRef.current?.querySelectorAll('input') || [];
    for (const field of fields) {
      if (!field.checkValidity()) {
        field.reportValidity();
        return;
      }
    }
    setFormError("");
    setStep(2);
  };

  // After the server accepts the form, check whether a session now exists.
  const sessionIsActive = async () => {
    try {
      await api.get('get/me/');
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearMessage();
    setFormError("");
    setFormSuccess("");

    const form = e.target;
    if (action === 'register' && step === 1) {
      goToStepTwo();
      return;
    }

    setLoading(true);
    let payload;

    if (action === 'login') {
      payload = {
        username: form.username?.value,
        password: form.password?.value,
      }
    }
    if (action === 'register') {
      payload = {
        username: form.username?.value,
        password: form.password?.value,
        email: form.email?.value,
        FirstName: form.FirstName?.value,
        LastName: form.LastName?.value,
        birthDate: form.birthDate?.value,
        phoneNumber: form.phoneNumber?.value,
        region: form.region?.value,
      }
    }

    try {
      await axios.post(`${API_BASE_URL + action}/`, payload, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });

      if (await sessionIsActive()) {
        // Full page load so every part of the app (navbar, cart) sees the new session.
        window.location.assign(nextPath);
        return;
      }

      setLoading(false);
      if (action === 'register') {
        // Account exists but the server did not sign the user in: ask them to log in.
        setPrefillUsername(payload.username || "");
        setAction('login');
        setFormSuccess(t('auth_ok_registered'));
      } else {
        setFormError(t('auth_err_session'));
      }
    } catch (err) {
      console.error(err);
      // Show the user why it failed: the server's own message if it sent one,
      // otherwise a translated fallback.
      const data = err.response?.data;
      const serverMsg = typeof data === 'string' ? '' : (data?.error || data?.detail || data?.message);
      if (!err.response) {
        setFormError(t('auth_err_network'));
      } else if (typeof serverMsg === 'string' && serverMsg.trim()) {
        setFormError(serverMsg);
      } else {
        setFormError(action === 'login' ? t('auth_err_login') : t('auth_err_register'));
      }
      setLoading(false);
    }
  };

  const passwordField = (controlled) => (
    <div className="form-group">
      <label htmlFor={`${action}-password`}>{t('auth_label_pass')}</label>
      <div className="password-wrapper">
        <input
          id={`${action}-password`}
          name="password"
          type={showPassword ? "text" : "password"}
          autoComplete={action === 'login' ? 'current-password' : 'new-password'}
          {...(controlled ? { value: password, onChange: (e) => setPassword(e.target.value) } : {})}
          required
        />
        <button
          type="button"
          className="password-toggle"
          onClick={() => setShowPassword(!showPassword)}
          tabIndex={-1}
          aria-label={showPassword ? "Hide password" : "Show password"}
        >
          <i className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"} />
        </button>
      </div>
    </div>
  );

  const messages = (
    <>
      {formSuccess && (
        <div className="auth-success" role="status">
          <i className="fas fa-circle-check" /> {formSuccess}
        </div>
      )}
      {formError && (
        <div className="auth-error" role="alert">
          <i className="fas fa-circle-exclamation" /> {formError}
        </div>
      )}
    </>
  );

  const submitButton = (label) => (
    <button className={`submit-btn ${loading ? "is-loading" : ""}`} disabled={loading}>
      {loading && <span className="btn-spinner" aria-hidden="true" />}
      <span>{label}</span>
    </button>
  );

  return (
    <div className="auth-page register-container">
      {/* Left: brand panel */}
      <div className="register-left">
        <Link to="/" className="auth-logo">
          <span className="auth-logo-mark"><i className="fas fa-seedling" /></span>
          <span>EcoNovda<span className="auth-logo-dot">.</span></span>
        </Link>
        <div className="auth-left-copy">
          <h1 className="hero-enter" style={{ '--d': '0.1s' }}>
            {t('auth_join_title')} <span className="highlight">{t('auth_join_highlight')}</span>
          </h1>
          <p className="hero-enter" style={{ '--d': '0.2s' }}>{t('auth_join_desc')}</p>
        </div>
        <div className="auth-photo hero-enter" style={{ '--d': '0.3s' }}>
          <img src="/images/home-hero.webp" alt="" />
        </div>
      </div>

      {/* Right: form */}
      <div className="register-right">
        <Link to="/" className="auth-back">
          <i className="fas fa-arrow-left" /> {t('nav_home')}
        </Link>
        <div className="auth-card">
          <div className={`auth-tabs ${action === "login" ? "is-login" : ""}`}>
            <span className="auth-tab-indicator" aria-hidden="true" />
            <button
              type="button"
              className={`auth-tab ${action === "register" ? "active" : ""}`}
              onClick={() => setAction("register")}
            >
              {t('auth_tab_register')}
            </button>
            <button
              type="button"
              className={`auth-tab ${action === "login" ? "active" : ""}`}
              onClick={() => setAction("login")}
            >
              {t('auth_tab_login')}
            </button>
          </div>

          {action === "register" ? (
            <form className="registration-form" onSubmit={handleSubmit} noValidate={false} key="register">
              {/* Step indicator */}
              <div className="auth-steps" aria-hidden="true">
                <span className={`auth-step ${step >= 1 ? 'on' : ''}`}><b>1</b> {t('auth_step_account')}</span>
                <span className="auth-step-line"><span style={{ transform: `scaleX(${step === 2 ? 1 : 0})` }} /></span>
                <span className={`auth-step ${step >= 2 ? 'on' : ''}`}><b>2</b> {t('auth_step_contact')}</span>
              </div>

              {/* Step 1: account. Kept in the DOM on step 2 so the values are submitted. */}
              <div className={`auth-step-panel ${step === 1 ? 'is-active' : ''}`} ref={stepOneRef}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="reg-first">{t('auth_label_fname')}</label>
                    <input id="reg-first" name="FirstName" autoComplete="given-name" required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="reg-last">{t('auth_label_lname')}</label>
                    <input id="reg-last" name="LastName" autoComplete="family-name" required />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="reg-username">{t('auth_label_username')}</label>
                  <input id="reg-username" name="username" autoComplete="username" required />
                </div>
                {passwordField(true)}
              </div>

              {/* Step 2: contact details */}
              <div className={`auth-step-panel ${step === 2 ? 'is-active' : ''}`}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="reg-phone">{t('auth_label_phone')}</label>
                    <input
                      id="reg-phone"
                      name="phoneNumber"
                      type="tel"
                      autoComplete="tel"
                      placeholder="+998 (__) ___-__-__"
                      value={phoneNumber}
                      onChange={handlePhoneChange}
                      maxLength={19}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="reg-dob">{t('auth_label_dob')}</label>
                    <input id="reg-dob" name="birthDate" type="date" autoComplete="bday" />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="reg-email">{t('auth_label_email')}</label>
                  <input id="reg-email" name="email" type="email" autoComplete="email" placeholder="email@example.com" />
                </div>
                <div className="form-group">
                  <label htmlFor="reg-region">{t('auth_label_region')}</label>
                  <select id="reg-region" name="region" defaultValue="">
                    <option value="">{t('auth_select_region')}</option>
                    {REGIONS.map(([value, key]) => (
                      <option key={value} value={value}>{t(key)}</option>
                    ))}
                  </select>
                </div>
              </div>

              {messages}

              {step === 1 ? (
                <button type="button" className="submit-btn" onClick={goToStepTwo}>
                  <span>{t('auth_btn_next')}</span> <i className="fas fa-arrow-right" />
                </button>
              ) : (
                <div className="auth-step-actions">
                  <button type="button" className="auth-btn-back" onClick={() => setStep(1)}>
                    <i className="fas fa-arrow-left" /> {t('auth_btn_back')}
                  </button>
                  {submitButton(t('auth_btn_create'))}
                </div>
              )}
            </form>
          ) : (
            <form className="registration-form" onSubmit={handleSubmit} key="login">
              <div className="form-group">
                <label htmlFor="login-username">{t('auth_label_username')}</label>
                <input
                  id="login-username"
                  name="username"
                  autoComplete="username"
                  defaultValue={prefillUsername}
                  required
                />
              </div>
              {passwordField(false)}
              {messages}
              {submitButton(t('auth_btn_login'))}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
